import { isAxiosError } from 'axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { builderToProper } from '../lib/schemaTransform';
import { postEmbedMessage } from '../lib/embedMessaging';
import type { AdminQuizStatusFilter, AdminQuizSummary, ResponseListSort, Survey } from '@responsedock/types';
import { useAuthStore } from '../store/auth';
import {
  getMockAnalyticsBySurveyId,
  getMockResponsesBySurveyId,
  getMockSurveyById,
  getMockSurveys,
  isMockSurveyId,
} from '../data/mockSurveyData';

export interface SurveyTemplate {
  id: string;
  workspaceId: string;
  name: string;
  description: string | null;
  category: string | null;
  schema: Record<string, unknown>;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export function useSurveys(
  workspaceId: string,
  options?: { page?: number; pageSize?: number; status?: string; search?: string },
) {
  const useMockData = useAuthStore((s) => s.useMockData);
  const page = options?.page ?? 1;
  const pageSize = options?.pageSize ?? 20;
  const status = options?.status ?? '';
  const search = options?.search ?? '';
  return useQuery({
    queryKey: ['surveys', workspaceId, page, pageSize, status, search, useMockData],
    queryFn: async () => {
      if (useMockData) {
        return { surveys: getMockSurveys(), total: 3 };
      }
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (status) params.set('status', status);
      if (search) params.set('search', search);
      const res = await api.get<{ surveys: Survey[]; total: number }>(`/workspaces/${workspaceId}/surveys?${params.toString()}`);
      return res.data;
    },
    enabled: !!workspaceId,
  });
}

export function useSurvey(surveyId: string) {
  const useMockData = useAuthStore((s) => s.useMockData);
  return useQuery({
    queryKey: ['survey', surveyId, useMockData],
    queryFn: async () => {
      if (useMockData && isMockSurveyId(surveyId)) {
        const survey = getMockSurveyById(surveyId);
        if (!survey) throw new Error('Mock survey not found');
        return survey;
      }
      const res = await api.get<{ survey: Survey }>(`/surveys/${surveyId}`);
      return res.data.survey;
    },
    enabled: !!surveyId,
  });
}

export function useCreateSurvey(workspaceId: string) {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async (data: { title: string; description?: string }) => {
      if (useMockData) throw new Error('Mock data mode is read-only. Disable it in Settings to create surveys.');
      const res = await api.post<{ survey: Survey }>(`/workspaces/${workspaceId}/surveys`, data);
      return res.data.survey;
    },
    onSuccess: (survey) => {
      postEmbedMessage({
        type: 'responsedock.questionnaire.created',
        questionnaire: { id: survey.id, slug: survey.slug, title: survey.title, status: survey.status },
      });
      void qc.invalidateQueries({ queryKey: ['surveys', workspaceId] });
    },
  });
}

export function usePublishSurvey() {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async (surveyId: string) => {
      if (useMockData && isMockSurveyId(surveyId)) throw new Error('Mock data mode is read-only.');
      const res = await api.post<{ version: object }>(`/surveys/${surveyId}/publish`);
      return res.data;
    },
    onSuccess: (_, surveyId) => {
      postEmbedMessage({ type: 'responsedock.questionnaire.published', surveyId });
      void qc.invalidateQueries({ queryKey: ['survey', surveyId] });
    },
  });
}

export type SurveyStatusAction = 'publish' | 'pause' | 'close' | 'archive';

/**
 * Change a survey's lifecycle status from outside the builder.
 *
 * `publish` resumes or reopens a survey. The API only publishes a draft version, so when
 * none exists the active (published) schema is first saved as a draft and then published.
 * When a draft exists, its saved changes are what gets published — the same as the builder.
 */
export function useSetSurveyStatus() {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async ({ surveyId, action }: { surveyId: string; action: SurveyStatusAction }) => {
      if (useMockData && isMockSurveyId(surveyId)) throw new Error('Mock data mode is read-only.');
      if (action === 'publish') {
        const current = await api.get<{ survey: Survey & { draftVersion?: { schema: object } | null } }>(`/surveys/${surveyId}`);
        const { draftVersion, activeVersion } = current.data.survey;
        if (!draftVersion && activeVersion?.schema) {
          await api.put(`/surveys/${surveyId}/draft`, { schema: activeVersion.schema });
        }
        const res = await api.post<{ version: object }>(`/surveys/${surveyId}/publish`);
        return res.data;
      }
      const res = await api.post<{ survey: Survey }>(`/surveys/${surveyId}/${action}`);
      return res.data;
    },
    onSuccess: (_, { surveyId, action }) => {
      if (action === 'publish') postEmbedMessage({ type: 'responsedock.questionnaire.published', surveyId });
      void qc.invalidateQueries({ queryKey: ['survey', surveyId] });
      void qc.invalidateQueries({ queryKey: ['surveys'] });
    },
  });
}

export function useSaveDraft() {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async ({ surveyId, schema }: { surveyId: string; schema: object }) => {
      if (useMockData && isMockSurveyId(surveyId)) throw new Error('Mock data mode is read-only.');
      // Transform builder format to proper API format before saving
      const properSchema = builderToProper(schema as Parameters<typeof builderToProper>[0]);
      const res = await api.put<{ version: object }>(`/surveys/${surveyId}/draft`, { schema: properSchema });
      return res.data;
    },
    onSuccess: (_, { surveyId }) => {
      postEmbedMessage({ type: 'responsedock.questionnaire.saved', surveyId });
      void qc.invalidateQueries({ queryKey: ['survey', surveyId] });
    },
  });
}

export function useUpdateSurvey() {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async ({ surveyId, data }: { surveyId: string; data: Partial<Survey> }) => {
      if (useMockData && isMockSurveyId(surveyId)) throw new Error('Mock data mode is read-only.');
      const res = await api.patch<{ survey: Survey }>(`/surveys/${surveyId}`, data);
      return res.data.survey;
    },
    onSuccess: (_, { surveyId }) => {
      void qc.invalidateQueries({ queryKey: ['survey', surveyId] });
    },
  });
}

export function useDeleteSurvey(workspaceId: string) {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async (surveyId: string) => {
      if (useMockData && isMockSurveyId(surveyId)) throw new Error('Mock data mode is read-only.');
      await api.delete(`/surveys/${surveyId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['surveys', workspaceId] });
    },
  });
}

export function useDeleteSurveyConfidentialData(workspaceId: string) {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async (surveyId: string) => {
      if (useMockData) throw new Error('Mock data mode is read-only.');
      const response = await api.delete<{ deletedAttachments: number; deletedAnswers: number }>(`/surveys/${surveyId}/confidential-data`);
      return response.data;
    },
    onSuccess: (_, surveyId) => {
      void qc.invalidateQueries({ queryKey: ['responses', surveyId] });
      void qc.invalidateQueries({ queryKey: ['surveys', workspaceId] });
    },
  });
}

export function useSurveyAnalytics(surveyId: string) {
  const useMockData = useAuthStore((s) => s.useMockData);
  return useQuery({
    queryKey: ['analytics', surveyId, useMockData],
    queryFn: async () => {
      if (useMockData && isMockSurveyId(surveyId)) {
        return getMockAnalyticsBySurveyId(surveyId) ?? {};
      }
      const res = await api.get<{ analytics: Record<string, unknown> }>(`/surveys/${surveyId}/analytics`);
      return res.data.analytics;
    },
    enabled: !!surveyId,
  });
}

export interface SurveyAnswer {
  id: string;
  questionId: string;
  value: unknown;
  createdAt: string;
  hiddenConfidentialCount?: number;
  confidential?: boolean;
  confidentialHidden?: boolean;
}

export interface SurveyResponseItem {
  id: string;
  surveyId: string;
  createdAt: string;
  completedAt: string | null;
  hiddenAt?: string | null;
  hiddenByUserId?: string | null;
  externalUserId: string | null;
  isAnonymous: boolean;
  /** Collection metadata; `channel: 'admin_generated'` marks generated sample responses. Empty for anonymous responses. */
  source?: { channel?: string } & Record<string, unknown>;
  /** Response context; `suspectedBot: true` when the honeypot or minimum-time heuristic fired. */
  context?: { suspectedBot?: boolean } & Record<string, unknown>;
  answers: SurveyAnswer[];
  /** Quiz score of a graded quiz response (anonymous ones included). */
  quiz?: AdminQuizSummary;
}

/** Server-side Responses filters, shared with the export endpoints. `from`/`to` are ISO dates or date-times. */
export interface SurveyResponseFilters {
  search?: string;
  from?: string;
  to?: string;
  /** Quiz surveys: passed, failed or pending review. */
  quizStatus?: AdminQuizStatusFilter;
}

/** Appends non-empty `search`, `from`, `to` and `quizStatus` filters to a query string. */
export function appendResponseFilterParams(params: URLSearchParams, filters: SurveyResponseFilters = {}): URLSearchParams {
  const search = filters.search?.trim();
  if (search) params.set('search', search);
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.quizStatus) params.set('quizStatus', filters.quizStatus);
  return params;
}

function mockResponseMatches(response: SurveyResponseItem, filters: SurveyResponseFilters): boolean {
  const createdAt = new Date(response.createdAt).getTime();
  if (filters.from && createdAt < new Date(filters.from).getTime()) return false;
  if (filters.to && createdAt > new Date(filters.to).getTime()) return false;
  if (filters.quizStatus && response.quiz?.status !== filters.quizStatus) return false;
  const needle = filters.search?.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [response.id, response.isAnonymous ? null : response.externalUserId, ...response.answers.map((answer) => JSON.stringify(answer.value ?? ''))];
  return haystack.some((text) => typeof text === 'string' && text.toLowerCase().includes(needle));
}

export function useSurveyResponses(
  surveyId: string,
  page = 1,
  pageSize = 50,
  includeHidden = false,
  focusResponseId?: string | null,
  includeIncomplete = true,
  filters: SurveyResponseFilters = {},
  sort: ResponseListSort = 'newest',
) {
  const useMockData = useAuthStore((s) => s.useMockData);
  const search = filters.search?.trim() || undefined;
  const from = filters.from || undefined;
  const to = filters.to || undefined;
  const quizStatus = filters.quizStatus || undefined;
  return useQuery({
    queryKey: ['responses', surveyId, page, pageSize, includeHidden, focusResponseId, includeIncomplete, useMockData, { search, from, to, quizStatus, sort }],
    // Keep showing the previous page of the same survey while a new page or filter loads.
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[1] === surveyId ? previous : undefined),
    queryFn: async () => {
      if (useMockData && isMockSurveyId(surveyId)) {
        const allResponses = getMockResponsesBySurveyId(surveyId).filter((response) => mockResponseMatches(response, { search, from, to, quizStatus }));
        const filtered = includeIncomplete ? allResponses : allResponses.filter((response) => response.completedAt);
        const scoreOf = (response: SurveyResponseItem) => response.quiz?.scorePercent ?? null;
        const responses = sort === 'newest' ? filtered : [...filtered].sort((left, right) => {
          const [a, b] = [scoreOf(left), scoreOf(right)];
          if (a === null || b === null) return a === b ? 0 : a === null ? 1 : -1;
          return sort === 'score_asc' ? a - b : b - a;
        });
        const focused = focusResponseId ? responses.find((response) => response.id === focusResponseId) : null;
        const pageResponses = responses.slice((page - 1) * pageSize, page * pageSize);
        return {
          responses: focused && !pageResponses.some((response) => response.id === focused.id)
            ? [focused, ...pageResponses]
            : pageResponses,
          total: responses.length,
          page,
          pageSize,
        };
      }
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('pageSize', String(pageSize));
      if (includeHidden) params.set('includeHidden', 'true');
      if (!includeIncomplete) params.set('status', 'completed');
      if (focusResponseId) params.set('responseId', focusResponseId);
      if (sort !== 'newest') params.set('sort', sort);
      appendResponseFilterParams(params, { search, from, to, quizStatus });
      const res = await api.get<{ responses: SurveyResponseItem[]; total: number; page: number; pageSize: number }>(
        `/surveys/${surveyId}/responses?${params.toString()}`,
      );
      return res.data;
    },
    enabled: !!surveyId,
  });
}

export function useDeleteResponse(surveyId: string) {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async (responseId: string) => {
      if (useMockData && isMockSurveyId(surveyId)) throw new Error('Mock data mode is read-only.');
      await api.delete(`/responses/${responseId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['responses', surveyId] });
      void qc.invalidateQueries({ queryKey: ['analytics', surveyId] });
    },
  });
}

export function useDeleteAttachment(surveyId: string) {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async (attachmentId: string) => {
      if (useMockData && isMockSurveyId(surveyId)) throw new Error('Mock data mode is read-only.');
      try {
        await api.delete(`/attachments/${attachmentId}`);
      } catch (error) {
        const data = isAxiosError(error) ? error.response?.data as { error?: unknown; message?: unknown } | undefined : undefined;
        const detail = typeof data?.error === 'string' && data.error !== 'Forbidden' ? data.error : data?.message;
        throw typeof detail === 'string' && detail.trim() ? new Error(detail) : error;
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['responses', surveyId] });
    },
  });
}

export function useHideResponse(surveyId: string) {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async (responseId: string) => {
      if (useMockData && isMockSurveyId(surveyId)) throw new Error('Mock data mode is read-only.');
      await api.post(`/responses/${responseId}/hide`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['responses', surveyId] });
      void qc.invalidateQueries({ queryKey: ['analytics', surveyId] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
      void qc.invalidateQueries({ queryKey: ['product-intelligence'] });
    },
  });
}

export function useUnhideResponse(surveyId: string) {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async (responseId: string) => {
      if (useMockData && isMockSurveyId(surveyId)) throw new Error('Mock data mode is read-only.');
      await api.post(`/responses/${responseId}/unhide`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['responses', surveyId] });
      void qc.invalidateQueries({ queryKey: ['analytics', surveyId] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
      void qc.invalidateQueries({ queryKey: ['product-intelligence'] });
    },
  });
}

export function useGenerateRandomResponses(surveyId: string) {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async (count: number) => {
      if (useMockData && isMockSurveyId(surveyId)) throw new Error('Mock data mode is read-only.');
      const res = await api.post<{ generated: number; responseIds: string[] }>(
        `/surveys/${surveyId}/responses/generate-random`,
        { count },
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['responses', surveyId] });
      void qc.invalidateQueries({ queryKey: ['analytics', surveyId] });
      void qc.invalidateQueries({ queryKey: ['overview'] });
      void qc.invalidateQueries({ queryKey: ['product-intelligence'] });
      void qc.invalidateQueries({ queryKey: ['surveys'] });
    },
  });
}

export function useDuplicateSurvey(workspaceId: string) {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async (surveyId: string) => {
      if (useMockData && isMockSurveyId(surveyId)) throw new Error('Mock data mode is read-only.');
      const res = await api.post<{ survey: Survey }>(`/surveys/${surveyId}/duplicate`);
      return res.data.survey;
    },
    onSuccess: (survey, sourceSurveyId) => {
      // questionnaire.created also posts the legacy survey.created (with sourceSurveyId) for older hosts.
      postEmbedMessage({
        type: 'responsedock.questionnaire.created',
        questionnaire: { id: survey.id, slug: survey.slug, title: survey.title, status: survey.status },
        sourceSurveyId,
      });
      void qc.invalidateQueries({ queryKey: ['surveys', workspaceId] });
    },
  });
}

// ─── Template hooks ────────────────────────────────────────────────────────────

export function useTemplates(workspaceId: string) {
  return useQuery({
    queryKey: ['templates', workspaceId],
    queryFn: async () => {
      const res = await api.get<{ templates: SurveyTemplate[] }>(`/workspaces/${workspaceId}/templates`);
      return res.data.templates;
    },
    enabled: !!workspaceId,
  });
}

export function useSaveAsTemplate(workspaceId: string) {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async ({ surveyId, name, description, category }: { surveyId: string; name: string; description?: string; category?: string }) => {
      if (useMockData && isMockSurveyId(surveyId)) throw new Error('Mock data mode is read-only.');
      const res = await api.post<{ template: SurveyTemplate }>(`/surveys/${surveyId}/save-as-template`, { name, description, category });
      return res.data.template;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['templates', workspaceId] });
    },
  });
}

export function useCreateTemplate(workspaceId: string) {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async (data: { name: string; description?: string; category?: string; schema: Record<string, unknown> }) => {
      if (useMockData) throw new Error('Mock data mode is read-only. Disable it in Settings to create templates.');
      const res = await api.post<{ template: SurveyTemplate }>(`/workspaces/${workspaceId}/templates`, data);
      return res.data.template;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['templates', workspaceId] });
    },
  });
}

export function useCreateSurveyFromTemplate(workspaceId: string) {
  const qc = useQueryClient();
  const useMockData = useAuthStore((s) => s.useMockData);
  return useMutation({
    mutationFn: async ({ templateId, title }: { templateId: string; title: string }) => {
      if (useMockData) throw new Error('Mock data mode is read-only. Disable it in Settings to create surveys.');
      const res = await api.post<{ survey: Survey }>(`/workspaces/${workspaceId}/templates/${templateId}/use`, { title });
      return res.data.survey;
    },
    onSuccess: (survey, { templateId }) => {
      // questionnaire.created also posts the legacy survey.created (with templateId) for older hosts.
      postEmbedMessage({
        type: 'responsedock.questionnaire.created',
        questionnaire: { id: survey.id, slug: survey.slug, title: survey.title, status: survey.status },
        templateId,
      });
      void qc.invalidateQueries({ queryKey: ['surveys', workspaceId] });
    },
  });
}

export function useDeleteTemplate(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (templateId: string) => {
      await api.delete(`/workspaces/${workspaceId}/templates/${templateId}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['templates', workspaceId] });
    },
  });
}
